import { Component, Vue } from 'nuxt-property-decorator';
import acceptConnectionRequestMutation from '@/apollo/mutations/Portfolio/AcceptConnectionRequest.mutation';
import connectionsQuery from '@/apollo/queries/Connections.query';
import currentUserUnseenNotificationsQuery from '@/components/global/HeaderMenu/HeaderMenuNotifications/CurrentUserUnseenNotifications.query';
import portfolioConnectionRequestsQuery from '@/apollo/queries/Portfolio/PortfolioConnectionRequests.query';
import readConnectionRequestsMutation from '@/apollo/mutations/Portfolio/ReadConnectionRequests.mutation';
import rejectConnectionRequestMutation from '@/apollo/mutations/Portfolio/RejectConnectionRequest.mutation';

import type { Connection, ConnectionRequestInbox } from '@/components/global/HeaderMenu/HeaderMenuConnections/types';
import type { ConnectionRequest } from '@/components/partials/public-portfolio/PublicPortfolioConnectionActions/types';
import type { CurrentUser } from '@/types/graphql';

const WEB_PLATFORM = 'WEB';

@Component<HeaderMenuConnectionsMixin>({
  name: 'HeaderMenuConnections',
  apollo: {
    connectionRequestsInbox: {
      query: connectionsQuery,
      skip() {
        return !this.$auth.loggedIn;
      },
    },
  },
})
export default class HeaderMenuConnectionsMixin extends Vue {
  protected connectionRequestsInbox: ConnectionRequestInbox = {
    edges: [],
    totalCount: 0,
  };

  get hasConnections(): boolean {
    return !!this.connectionRequestsInbox.totalCount;
  }

  get connections(): Connection[] {
    return this.connectionRequestsInbox.edges.map(({ node }) => node);
  }

  get unreadConnectionIDs(): string[] {
    return this.connectionRequestsInbox.edges.reduce(
      (unread, edge) => (edge.node.read ? unread : [...unread, edge.node.id]),
      [] as string[],
    );
  }

  get hasUnreadConnections(): boolean {
    return !!this.unreadConnectionIDs.length;
  }

  async readConnectionRequest(id: string): Promise<void> {
    try {
      await this.$apollo.mutate({
        mutation: readConnectionRequestsMutation,
        variables: {
          input: { ids: [id] },
        },
        update: store => {
          const connectionQuery = {
            query: connectionsQuery,
          };

          const connectionData = store.readQuery<{ connectionRequestsInbox: ConnectionRequestInbox }>(connectionQuery);

          if (connectionData) {
            connectionData.connectionRequestsInbox = {
              ...connectionData.connectionRequestsInbox,
              edges: connectionData.connectionRequestsInbox.edges.map(edge =>
                edge.node.id === id
                  ? {
                      ...edge,
                      node: { ...edge.node, read: true },
                    }
                  : edge,
              ),
            };

            store.writeQuery({ ...connectionQuery, data: connectionData });
          }

          const currentUserQuery = {
            query: currentUserUnseenNotificationsQuery,
            variables: {
              platform: WEB_PLATFORM,
            },
          };

          const currentUserData = store.readQuery<{ currentUser: CurrentUser }>(currentUserQuery);

          if (currentUserData) {
            currentUserData.currentUser = {
              ...currentUserData.currentUser,
              unreadConnectionRequestsCount: currentUserData.currentUser.unreadConnectionRequestsCount - 1,
            };

            store.writeQuery({ ...currentUserQuery, data: currentUserData });
          }
        },
      });
    } catch (error) {
      if (error instanceof Error) {
        this.$notice.error('Failed to open connection request, try again in few minutes.', error);
      }
    }
  }

  async readAllConnectionRequest(): Promise<void> {
    try {
      await this.$apollo.mutate({
        mutation: readConnectionRequestsMutation,
        variables: {
          input: { ids: this.unreadConnectionIDs },
        },
        update: store => {
          const connectionQuery = {
            query: connectionsQuery,
          };

          const connectionData = store.readQuery<{ connectionRequestsInbox: ConnectionRequestInbox }>(connectionQuery);

          if (connectionData) {
            connectionData.connectionRequestsInbox = {
              ...connectionData.connectionRequestsInbox,
              edges: connectionData.connectionRequestsInbox.edges.map(edge => ({
                ...edge,
                node: { ...edge.node, read: true },
              })),
            };

            store.writeQuery({ ...connectionQuery, data: connectionData });
          }

          const currentUserQuery = {
            query: currentUserUnseenNotificationsQuery,
            variables: {
              platform: WEB_PLATFORM,
            },
          };

          const currentUserData = store.readQuery<{ currentUser: CurrentUser }>(currentUserQuery);

          if (currentUserData) {
            currentUserData.currentUser = {
              ...currentUserData.currentUser,
              unreadConnectionRequestsCount: 0,
            };

            store.writeQuery({ ...currentUserQuery, data: currentUserData });
          }
        },
      });
    } catch (error) {
      if (error instanceof Error) {
        this.$notice.error('Failed to read all connection request, try again in few minutes.', error);
      }
    }
  }

  async acceptConnectionRequest(id: string, slug: string): Promise<void> {
    try {
      await this.$apollo.mutate({
        mutation: acceptConnectionRequestMutation,
        variables: {
          input: { id },
        },
        refetchQueries: [{ query: portfolioConnectionRequestsQuery, variables: { slug } }],
        update: (store, { data: { incomingConnectionRequest } }) => {
          const connectionQuery = {
            query: connectionsQuery,
          };

          const connectionData = store.readQuery<{ connectionRequestsInbox: ConnectionRequestInbox }>(connectionQuery);

          if (connectionData) {
            connectionData.connectionRequestsInbox = {
              ...connectionData.connectionRequestsInbox,
              edges: connectionData.connectionRequestsInbox.edges.map(edge =>
                edge.node.id === (incomingConnectionRequest as ConnectionRequest).id
                  ? {
                      ...edge,
                      node: {
                        ...edge.node,
                        read: true,
                        status: (incomingConnectionRequest as ConnectionRequest).status,
                      },
                    }
                  : edge,
              ),
            };

            store.writeQuery({ ...connectionQuery, data: connectionData });
          }

          const currentUserQuery = {
            query: currentUserUnseenNotificationsQuery,
            variables: {
              platform: WEB_PLATFORM,
            },
          };

          const currentUserData = store.readQuery<{ currentUser: CurrentUser }>(currentUserQuery);

          if (currentUserData) {
            currentUserData.currentUser = {
              ...currentUserData.currentUser,
              unreadConnectionRequestsCount: currentUserData.currentUser.unreadConnectionRequestsCount - 1,
            };

            store.writeQuery({ ...currentUserQuery, data: currentUserData });
          }
        },
      });
    } catch (error) {
      if (error instanceof Error) {
        this.$notice.error('Failed to accept connection request, try again in few minutes.', error);
      }
    }
  }

  async declineConnectionRequest(id: string, slug: string): Promise<void> {
    try {
      await this.$apollo.mutate({
        mutation: rejectConnectionRequestMutation,
        variables: {
          input: { id },
        },
        refetchQueries: [{ query: portfolioConnectionRequestsQuery, variables: { slug } }],
        update: (store, { data: { incomingConnectionRequest } }) => {
          const connectionQuery = {
            query: connectionsQuery,
          };

          const connectionData = store.readQuery<{ connectionRequestsInbox: ConnectionRequestInbox }>(connectionQuery);

          if (connectionData) {
            connectionData.connectionRequestsInbox = {
              ...connectionData.connectionRequestsInbox,
              edges: connectionData.connectionRequestsInbox.edges.map(edge =>
                edge.node.id === (incomingConnectionRequest as ConnectionRequest).id
                  ? {
                      ...edge,
                      node: {
                        ...edge.node,
                        read: true,
                        status: (incomingConnectionRequest as ConnectionRequest).status,
                      },
                    }
                  : edge,
              ),
            };

            store.writeQuery({ ...connectionQuery, data: connectionData });
          }

          const currentUserQuery = {
            query: currentUserUnseenNotificationsQuery,
            variables: {
              platform: WEB_PLATFORM,
            },
          };

          const currentUserData = store.readQuery<{ currentUser: CurrentUser }>(currentUserQuery);

          if (currentUserData) {
            currentUserData.currentUser = {
              ...currentUserData.currentUser,
              unreadConnectionRequestsCount: currentUserData.currentUser.unreadConnectionRequestsCount - 1,
            };

            store.writeQuery({ ...currentUserQuery, data: currentUserData });
          }
        },
      });
    } catch (error) {
      if (error instanceof Error) {
        this.$notice.error('Failed to decline connection request, try again in few minutes.', error);
      }
    }
  }
}
